import { useNavigate } from "react-router-dom";
import {
  FlaskConicalIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircle2Icon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { t } from "@/lib/typography";
import type { PracticeLabResponse } from "@/types/practice-lab";

// Derived progress badge. Nothing renders for a not-started lab (progress
// undefined), mirroring how BookCard shows no status until a book is started.
function StatusBadge({ lab }: { lab: PracticeLabResponse }) {
  const progress = lab.progress;
  if (!progress) return null;

  if (progress.status === "completed") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400">
        <CheckCircle2Icon className="size-3.5" />
        Completed
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      In progress · {progress.verified_count}/{lab.step_count}
    </span>
  );
}

// Dashboard listing row for a standalone practice lab. App-themed (dark-capable),
// mirroring BookCard's clickable-card pattern — distinct from the light-only lab
// page components under components/practice-lab/.
export function PracticeLabCard({ lab }: { lab: PracticeLabResponse }) {
  const navigate = useNavigate();
  const open = () => navigate(`/practice-labs/${lab.id}`);

  return (
    <Card
      className="group cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`Open ${lab.title}`}
    >
      <CardContent className="flex items-center gap-4 p-4 sm:p-5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-foreground">
          <FlaskConicalIcon className="size-5" />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className={`${t.sectionTitle} truncate`}>{lab.title}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`${t.meta} capitalize`}>{lab.technology}</span>
            <span className={`${t.meta} capitalize`}>{lab.difficulty}</span>
            <span className={`${t.meta} flex items-center gap-1`}>
              <ClockIcon className="size-3.5" />~{lab.est_minutes} min
            </span>
            <span className={t.meta}>{lab.step_count} steps</span>
          </div>
        </div>

        <StatusBadge lab={lab} />

        <ChevronRightIcon
          aria-hidden
          className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
        />
      </CardContent>
    </Card>
  );
}
